import { takeLatest, put, call, select } from "redux-saga/effects";

import { canvasInfoActions } from "actions/canvasInfoActions.js";
import { buyplanSummaryBuyplanItemsSelector } from "selectors/buyplanSummarySelector.js";

import { GET_DATA } from "actions/apiOperations.js";
import { PATCH_DATA } from "actions/apiOperations.js";
import { DELETE_DATA } from "actions/apiOperations.js";

// Helper function to normalize month parameter for API calls
const normalizeMonthForAPI = (month) => {
  // Convert "SUGGESTED ITEMS" to "SUGGESTED_ITEMS" for backend API calls
  if (month === "SUGGESTED ITEMS") {
    return "SUGGESTED_ITEMS";
  }
  return month;
};


export const requestCanvasInfo = function* (action) {
  const { buyplanId, month, pageId, ...params } = action.payload;
  
  // Get the page from URL if not provided. Don't silently default to 'page1'.
  let currentPage = pageId;
  if (!currentPage) {
    const pathParts = window.location.pathname.split('/');
    const lastPart = pathParts[pathParts.length - 1];
    currentPage = lastPart && lastPart.startsWith('page') ? lastPart : '';
  }
  
  // If no page could be determined, do not call the page-specific endpoint.
  if (!currentPage) {
    // Caller should fetch pages list (`requestCanvasPages`) and decide which
    // page to request. Return early to avoid requests to `/page/` without id.
    return;
  }

  // Use the full page identifier (e.g., 'page3' instead of '3')
  yield put({
    type: GET_DATA,
    path: `/api/buyplans/${buyplanId}/canvas_state/${normalizeMonthForAPI(month)}?page=${currentPage}`,
    actions: canvasInfoActions,
    params
  });
};

export const updateCanvasInfo = function* (action) {
  const { buyplanId, month, canvasData, pageId, pageTitle } = action.payload;

  // Get the page from URL if not provided
  let currentPage = pageId;
  if (!currentPage) {
    const pathParts = window.location.pathname.split('/');
    const lastPart = pathParts[pathParts.length - 1];
    currentPage = lastPart && lastPart.startsWith('page') ? lastPart : '';
  }

  if (!currentPage) {
    return;
  }

  // Get buyplan items data to map buyplan item IDs to actual product IDs
  const buyplanItems = yield select(buyplanSummaryBuyplanItemsSelector);
  
  // Create a mapping from buyplan item ID to actual product ID
  const buyplanItemToProductIdMap = new Map();
  if (buyplanItems && typeof buyplanItems.entrySeq === 'function') {
    buyplanItems.entrySeq().forEach(([, monthData]) => {
      const items = monthData.get('items');
      if (items && typeof items.forEach === 'function') {
        items.forEach(item => {
          const buyplanItemId = item.get('id');
          const actualProductId = item.get('product_id');
          if (buyplanItemId && actualProductId) {
            buyplanItemToProductIdMap.set(String(buyplanItemId), actualProductId);
          }
        });
      }
    });
  }

  // Extract product IDs from canvas objects
  const extractProductIds = (canvasData) => {
    const productIds = new Set();
    
    if (canvasData && canvasData.objects && Array.isArray(canvasData.objects)) {
      const extractFromObjects = (objects) => {
        objects.forEach(obj => {
          if (obj && typeof obj === 'object') {
            // Check if this object has a product_id
            if (obj.product_id && obj.product_id !== '') {
              const productIdValue = String(obj.product_id);
              
              // First, try to use the product_id as-is (in case it's already the actual product ID)
              // Check if it's a valid product ID by looking it up in our mapping
              let actualProductId = null;
              
              // Check if this product_id is already the actual product ID
              for (const [, mappedProductId] of buyplanItemToProductIdMap.entries()) {
                if (String(mappedProductId) === productIdValue) {
                  actualProductId = mappedProductId;
                  break;
                }
              }
              
              // If not found as actual product ID, treat it as buyplan item ID and look it up
              if (!actualProductId) {
                actualProductId = buyplanItemToProductIdMap.get(productIdValue);
              }
              
              if (actualProductId) {
                productIds.add(actualProductId);
              }
            }
            
            // If this object has nested objects (like groups), recurse
            if (obj.objects && Array.isArray(obj.objects)) {
              extractFromObjects(obj.objects);
            }
          }
        });
      };
      
      extractFromObjects(canvasData.objects);
    }
    
    return Array.from(productIds).sort((a, b) => a - b);
  };

  const productIds = extractProductIds(canvasData);

  // Include page_title and product_ids in the canvas_state payload
  const payload = {
    canvas_state: {
      ...canvasData,
      page_title: pageTitle || null,
      product_ids: productIds
    }
  };

  // Use the full page identifier (e.g., 'page3' instead of '3')
  yield put({
    type: PATCH_DATA,
    path: `/api/buyplans/${buyplanId}/canvas_state/${normalizeMonthForAPI(month)}?page=${currentPage}`,
    actions: canvasInfoActions,
    params: payload,
  });
};

export const updatePageCanvas = function* (action) {
  const { buyplanId, month, pageId, canvasData, pageTitle } = action.payload;

  // Get the page from URL if not provided
  let currentPage = pageId;
  if (!currentPage) {
    const pathParts = window.location.pathname.split('/');
    const lastPart = pathParts[pathParts.length - 1];
    currentPage = lastPart && lastPart.startsWith('page') ? lastPart : '';
  }

  if (!currentPage) {
    return;
  }

  // Get buyplan items data to map buyplan item IDs to actual product IDs
  const buyplanItems = yield select(buyplanSummaryBuyplanItemsSelector);
  
  // Create a mapping from buyplan item ID to actual product ID
  const buyplanItemToProductIdMap = new Map();
  if (buyplanItems && typeof buyplanItems.entrySeq === 'function') {
    buyplanItems.entrySeq().forEach(([, monthData]) => {
      const items = monthData.get('items');
      if (items && typeof items.forEach === 'function') {
        items.forEach(item => {
          const buyplanItemId = item.get('id');
          const actualProductId = item.get('product_id');
          if (buyplanItemId && actualProductId) {
            buyplanItemToProductIdMap.set(String(buyplanItemId), actualProductId);
          }
        });
      }
    });
  }

  // Extract product IDs from canvas objects
  const extractProductIds = (canvasData) => {
    const productIds = new Set();
    
    if (canvasData && canvasData.objects && Array.isArray(canvasData.objects)) {
      const extractFromObjects = (objects) => {
        objects.forEach(obj => {
          if (obj && typeof obj === 'object') {
            // Check if this object has a product_id
            if (obj.product_id && obj.product_id !== '') {
              const productIdValue = String(obj.product_id);
              
              // First, try to use the product_id as-is (in case it's already the actual product ID)
              // Check if it's a valid product ID by looking it up in our mapping
              let actualProductId = null;
              
              // Check if this product_id is already the actual product ID
              for (const [, mappedProductId] of buyplanItemToProductIdMap.entries()) {
                if (String(mappedProductId) === productIdValue) {
                  actualProductId = mappedProductId;
                  break;
                }
              }
              
              // If not found as actual product ID, treat it as buyplan item ID and look it up
              if (!actualProductId) {
                actualProductId = buyplanItemToProductIdMap.get(productIdValue);
              }
              
              if (actualProductId) {
                productIds.add(actualProductId);
              }
            }
            
            // If this object has nested objects (like groups), recurse
            if (obj.objects && Array.isArray(obj.objects)) {
              extractFromObjects(obj.objects);
            }
          }
        });
      };
      
      extractFromObjects(canvasData.objects);
    }
    
    return Array.from(productIds).sort((a, b) => a - b);
  };

  const productIds = extractProductIds(canvasData);

  // Include page_title and product_ids in the canvas_state payload
  const payload = {
    canvas_state: {
      ...canvasData,
      page_title: pageTitle || null,
      product_ids: productIds
    }
  };

  // Provide an onSuccess handler so the PATCH response can update the
  // pages list (page_titles) immediately without requiring a separate
  // GET. This keeps the server as the source of truth.
  const onSuccess = (response) => {
    const { data } = response || {};
    const pageTitles = data && data.page_titles ? data.page_titles : {};

    // Filter out page0 and null titles
    const validPages = Object.entries(pageTitles || {})
      .filter(([pid, title]) => pid !== 'page0' && title !== null)
      .reduce((acc, [pid, title]) => {
        acc[pid] = title;
        return acc;
      }, {});

    return {
      type: canvasInfoActions.successPages,
      payload: {
        data: {
          page_titles: validPages
        }
      }
    };
  };

  // Use the full page identifier (e.g., 'page3' instead of '3')
  yield put({
    type: PATCH_DATA,
    path: `/api/buyplans/${buyplanId}/canvas_state/${normalizeMonthForAPI(month)}?page=${currentPage}`,
    actions: canvasInfoActions,
    params: payload,
    onSuccess
  });
};

export const watchCanvasInfo = function* () {
  yield takeLatest(
    canvasInfoActions.request,
    requestCanvasInfo
  );
};

// Handle page deletion via the DELETE endpoint
export function* removeCanvasPage(action) {
  if (!action?.payload) {
    yield put({
      type: canvasInfoActions.failure,
      error: 'No payload provided for page deletion'
    });
    return;
  }
  const { buyplanId, month, pageId } = action.payload;

  // Make sure we have the required parameters
  if (!buyplanId || !month || !pageId) {
    // Cannot process without required parameters
    yield put({
      type: canvasInfoActions.failure,
      error: 'Missing parameters for page deletion'
    });
    return;
  }

  try {
    // Create success handler to process the response
    const onSuccess = (response) => {
      const { data } = response;
      const pageTitles = data.page_titles || {};

      // Filter out page0 and null titles
      const validPages = Object.entries(pageTitles)
        .filter(([pageId, title]) => pageId !== 'page0' && title !== null)
        .reduce((acc, [pageId, title]) => {
          acc[pageId] = title;
          return acc;
        }, {});

      // Create and dispatch custom event
      const successEvent = new CustomEvent('page-deletion-success', {
        detail: {
          message: data.message || 'Page deleted successfully',
          pageId: pageId,
          pageName: `Page ${pageId.replace('page', '')}`,
          pageTitles: validPages,
          totalPages: data.total_pages || 0
        }
      });

      // Mark that the event was fired
      document.deleteSuccessEventFired = true;

      // Dispatch the event
      document.dispatchEvent(successEvent);

      // Return success action with filtered page titles
      return {
        type: canvasInfoActions.success,
        payload: {
          data: {
            ...data,
            page_titles: validPages
          }
        }
      };
    };

    // Send the DELETE request using the project's standard pattern. Do not
    // block here — let the component handle post-delete refresh. The
    // onSuccess handler above dispatches a DOM event ('page-deletion-success')
    // and returns the canvasInfoActions.success action so components or
    // reducers can react when the API call completes.
    // Use the correct API route: /canvas_state/:month?page=pageId
    yield put({
      type: DELETE_DATA,
      path: `/api/buyplans/${buyplanId}/canvas_state/${normalizeMonthForAPI(month)}?page=${pageId}`,
      actions: canvasInfoActions,
      params: {},
      onSuccess
    });

    // Do not wait inside this saga — the UI component will listen for the
    // 'page-deletion-success' event (or the canvasInfoActions.success action)
    // and then call getCanvasPages(...) to reload pages for the month.
    return;
  } catch (error) {
    // Handle errors properly through the Redux action
    yield put({
      type: canvasInfoActions.failure,
      error: error.message || 'Error deleting page'
    });
  }
};

export const watchRemoveCanvasPage = function* () {
  yield takeLatest(canvasInfoActions.removePage, function* (action) {
    try {
      yield call(removeCanvasPage, action);
    } catch (error) {
      yield put({
        type: canvasInfoActions.failure,
        error: error.message || 'Failed to delete page'
      });
    }
  });
};

export const watchUpdateCanvasInfo = function* () {
  yield takeLatest(
    canvasInfoActions.update,
    updateCanvasInfo
  );
};

export const watchUpdatePageCanvas = function* () {
  yield takeLatest(
    canvasInfoActions.updatePageCanvas,
    updatePageCanvas
  );
};

// Canvas pages list saga
export const requestCanvasPages = function* (action) {
  const { buyplanId, month, ...params } = action.payload;

  yield put({
    type: GET_DATA,
    path: `/api/buyplans/${buyplanId}/canvas_state/${normalizeMonthForAPI(month)}`,
    actions: {
      success: canvasInfoActions.successPages,
      failure: canvasInfoActions.failurePages,
      startLoader: canvasInfoActions.startLoader
    },
    params
  });
};

export const watchCanvasPages = function* () {
  yield takeLatest(
    canvasInfoActions.requestPages,
    requestCanvasPages
  );
};

// Canvas state with all products saga
export const requestCanvasStateAll = function* (action) {
  const { buyplanId, month } = action.payload;

  yield put({
    type: GET_DATA,
    path: `/api/buyplans/${buyplanId}/canvas_state/${normalizeMonthForAPI(month)}?all`,
    actions: {
      success: canvasInfoActions.successCanvasStateAll,
      failure: canvasInfoActions.failureCanvasStateAll,
      startLoader: canvasInfoActions.startCanvasStateAllLoader
    }
  });
};

export const watchCanvasStateAll = function* () {
  yield takeLatest(
    canvasInfoActions.requestCanvasStateAll,
    requestCanvasStateAll
  );
};

// Handle page renaming via the PATCH endpoint
export function* renameCanvasPageSaga(action) {
  if (!action.payload) {
    yield put({
      type: canvasInfoActions.failure,
      error: 'No payload provided for page renaming'
    });
    return;
  }
  
  const { buyplanId, month, pageId, pageName } = action.payload;

  // Make sure we have the required parameters
  if (!buyplanId || !month || !pageId || !pageName) {
    yield put({
      type: canvasInfoActions.failure,
      error: 'Missing parameters for page renaming'
    });
    return;
  }

  try {
    // Create success handler to process the response
    const onSuccess = (response) => {
      const { data } = response;
      const pageTitles = data.page_titles || {};

      // Filter out page0 and null titles
      const validPages = Object.entries(pageTitles)
        .filter(([pid, title]) => pid !== 'page0' && title !== null)
        .reduce((acc, [pid, title]) => {
          acc[pid] = title;
          return acc;
        }, {});

      // Return success action with updated page titles
      return {
        type: canvasInfoActions.success,
        payload: {
          data: {
            ...data,
            page_titles: validPages
          }
        }
      };
    };

    // Send the PATCH request to rename the page
    // The API expects the full canvas_state structure with page_title inside
    yield put({
      type: PATCH_DATA,
      path: `/api/buyplans/${buyplanId}/canvas_state/${normalizeMonthForAPI(month)}?page=${pageId}`,
      actions: canvasInfoActions,
      params: {
        canvas_state: {
          version: "5.5.2",
          objects: [],
          page_title: pageName
        }
      },
      onSuccess
    });

    return;
  } catch (error) {
    // Handle errors properly through the Redux action
    yield put({
      type: canvasInfoActions.failure,
      error: error.message || 'Error renaming page'
    });
  }
}

export const watchRenameCanvasPage = function* () {
  yield takeLatest(
    canvasInfoActions.renamePage,
    renameCanvasPageSaga
  );
};

// Add watcher to the list of exported sagas
export default [
  watchCanvasInfo,
  watchUpdateCanvasInfo,
  watchUpdatePageCanvas,
  watchRemoveCanvasPage,
  watchCanvasPages,
  watchCanvasStateAll,
  watchRenameCanvasPage
];